import type { IconType } from "react-icons";
import {
  FiHome,
  FiCheckSquare,
  FiBell,
  FiMessageSquare,
  FiMail,
  FiFileText,
  FiPlusCircle,
  FiClock,
  FiAlertTriangle,
  FiSearch,
  FiTool,
  FiShield,
  FiBarChart2,
  FiSettings,
  FiUsers,
  FiDatabase,
  FiCalendar,
  FiActivity,
  FiLock,
  FiFolder,
  FiUserCheck,
  FiGitMerge,
  FiClipboard,
  FiLink,
  FiMonitor,
  FiGrid,
  FiUser,
  FiTrendingUp,
  FiCheckCircle,
  FiBriefcase,
  FiXCircle,
  FiRefreshCcw,
  FiCircle,
  FiPieChart,
  FiPaperclip,
  FiAlertCircle,
  FiOctagon,
  FiHelpCircle,
  FiLayers,
  FiBox,
  FiCpu,
  FiGitBranch,
  FiBarChart,
  FiSliders,
  FiRotateCcw,
} from "react-icons/fi";
import { buildCmtsMenuFromManifest } from "./buildCmtsMenuFromManifest.js";
import { FaFileCircleCheck } from "react-icons/fa6";

export type CmtsMenuItem = {
  icon?: IconType | undefined;
  id: string;
  label: string;
  path: string;
  permission: string;
  description?: string | undefined;
  badge?: string | undefined;
};

export type CmtsMenuGroup = {
  id: string;
  label: string;
  items: readonly CmtsMenuItem[];
};

const cmtsMenuIconById: Record<string, IconType> = {
  "workspace": FiHome,
  "tasks": FiCheckSquare,
  "notifications": FiBell,
  "messages": FiMessageSquare,
  "complaint-register": FiFileText,
  "create-complaint": FiPlusCircle,
  "sla-tracker": FiClock,
  "escalations": FiAlertTriangle,
  "rca-dashboard": FiGrid,
  "rca-queue": FiFileText,
  "capa-queue": FiShield,
  "capa": FiShield,
  "capa-actions": FiShield,
  "rca-reports": FiBarChart2,
  "rca-fmea": FiLayers,
  "rca-settings": FiSettings,
  "inv-overview": FiActivity,
  "inv-method": FiSliders,
  "inv-evidence": FiPaperclip,
  "inv-actions": FiCheckSquare,
  "inv-team": FiUsers,
  "inv-review": FiShield,
  "inv-audit": FiClock,
  "inv-fmea": FiLayers,
  "inv-lifecycle": FiRotateCcw,
  "verification-queue": FiShield,
  "management-dashboard": FiTrendingUp,
  "quality-dashboard": FiCheckCircle,
  "department-dashboard": FiGrid,
  "personal-dashboard": FiUser,
  "customer-dashboard": FiUsers,
  "supplier-dashboard": FiActivity,
  "reports": FiBarChart2,
  "typed-reports": FiFileText,
  "sla-reports": FiFileText,
  "ageing-report": FiFileText,
  "department-performance-report": FiFileText,
  "customer-report": FiFileText,
  "supplier-report": FiFileText,
  "rca-capa-reports": FiFileText,
  "closure-verification-report": FiFileText,
  "audit-report": FiFileText,
  "security-report": FiFileText,
  "report-exports": FiFolder,
  "report-export-history": FiFolder,
  "master-data": FiDatabase,
  "reference-data": FiDatabase,
  "organization": FiUsers,
  "routing": FiSettings,
  "field-schemas": FiSettings,
  "sod": FiUserCheck,
  "configuration": FiSettings,
  "dependencies": FiGitMerge,
  "calendar": FiCalendar,
  "scheduled-jobs": FiClock,
  "audit": FiClipboard,
  "audit-history": FiClipboard,
  "security": FiLock,
  "access-decisions": FiLock,
  "readiness": FiActivity,
  "endpoint-coverage": FiLink,
  "system-operations": FiMonitor,
  "notifications.cmts.admin.notifications": FiMail,
  "admin-notifications": FiMail,
  "email-templates": FiMail,
  "admin-email-templates": FiMail,
  "master-data.complaint-sources": FiUsers,
  "master-data.complaint-types": FiBriefcase,
  "master-data.complaint-categories": FiFolder,
  "master-data.complaint-subcategories": FiFolder,
  "master-data.priorities": FiAlertTriangle,
  "master-data.severities": FiXCircle,
  "master-data.workflow-statuses": FiRefreshCcw,
  "master-data.workflow-stages": FaFileCircleCheck,
  "master-data.rca-methods": FiPieChart,
  "master-data.root-cause-categories": FiSearch,
  "master-data.capa-action-types": FiCheckSquare,
  "master-data.verification-methods": FiSearch,
  "master-data.evidence-types": FiPaperclip,
  "master-data.defect-modes": FiAlertCircle,
  "master-data.failure-modes": FiOctagon,
  "master-data.containment-types": FiShield,
  "master-data.reason-codes": FiHelpCircle,
  "master-data.closure-codes": FiClock,
  "master-data.departments": FiUsers,
  "master-data.sites": FiHome,
  "master-data.product-families": FiLayers,
  "master-data.products": FiBox,
  "master-data.parts": FiCpu,
  "master-data.processes": FiGitBranch,
  "master-data.lines": FiActivity,
  "master-data.machines": FiSettings,
  "master-data.notification-templates": FiMail,
  "master-data.message-templates": FiMessageSquare,
  "master-data.report-types": FiBarChart,
  "master-data.dashboard-kpis": FiTrendingUp
};

export const cmtsEnterpriseMenuGroups: readonly CmtsMenuGroup[] = buildCmtsMenuFromManifest().map((group) => ({
  id: group.id,
  label: group.label,
  items: group.items.map((item) => ({
    ...item,
    icon: cmtsMenuIconById[item.id],
  })),
}));

export function filterCmtsEnterpriseMenuGroups(
  permissions: readonly string[] | undefined,
  isRcaOwnerOrAdmin?: boolean,
  isCapaOwnerOrAdmin?: boolean,
  isVerificationOwnerOrAdmin?: boolean,
  isRcaInvestigation?: boolean,
): readonly CmtsMenuGroup[] {
  const set = new Set(permissions ?? []);
  let filtered = cmtsEnterpriseMenuGroups
    .map((group) => ({ ...group, items: group.items.filter((item) => item.permission === "PUBLIC" || set.has(item.permission)) }))
    .filter((group) => group.items.length > 0);

  if (!isRcaOwnerOrAdmin) {
    filtered = filtered.filter((g) => g.id !== "rca-capa");
  }
  if (!isRcaInvestigation) {
    filtered = filtered.filter((g) => g.id !== "investigation-navigation");
  }
  if (!isCapaOwnerOrAdmin) {
    filtered = filtered.filter((g) => g.id !== "capa");
  }
  if (!isVerificationOwnerOrAdmin) {
    filtered = filtered.filter((g) => g.id !== "verification");
  }
  return filtered;
}

export function isCmtsPathActive(activePath: string | undefined, itemPath: string): boolean {
  const current = (activePath ?? "/cmts").split("?")[0] ?? "/cmts";
  const target = itemPath.split("?")[0] ?? itemPath;
  if (target === "/cmts") return current === target;
  return current === target || (target !== "/cmts/complaints" && current.startsWith(`${target}/`));
}

export function cmtsBreadcrumbsForPath(path: string | undefined): readonly string[] {
  const clean = (path ?? "/cmts").split("?")[0] ?? "/cmts";
  const match = cmtsEnterpriseMenuGroups.flatMap((group) => group.items.map((item) => ({ group: group.label, item }))).find((entry) => isCmtsPathActive(clean, entry.item.path));
  if (match) return ["CMTS", match.group, match.item.label];
  if (clean.includes("/complaints/") && clean.endsWith("/workflow")) return ["CMTS", "Complaint Management", "Workflow"];
  if (clean.includes("/complaints/") && clean.endsWith("/evidence")) return ["CMTS", "Complaint Management", "Evidence"];
  if (clean.includes("/complaints/") && clean.endsWith("/rca")) return ["CMTS", "RCA / CAPA", "RCA"];
  if (clean.includes("/complaints/") && clean.endsWith("/capa")) return ["CMTS", "CAPA", "CAPA"];
  if (clean.includes("/complaints/") && clean.endsWith("/verification")) return ["CMTS", "Verification", "Verification"];
  if (clean.includes("/complaints/")) return ["CMTS", "Complaint Management", "Complaint Detail"];
  return ["CMTS"];
}

export const cmtsReportNavigationLabelProof = ["Report Exports", "Export History", "RCA / CAPA Reports"] as const;

export type CmtsBackendMenuLike = {
  readonly id: string;
  readonly label: string;
  readonly href?: string | undefined;
  readonly permission?: string | undefined;
  readonly parentId?: string | undefined;
  readonly order?: number | undefined;
};

export function buildCmtsMenuGroupsFromBackendMenu(
  backendItems: readonly CmtsBackendMenuLike[],
  permissions: readonly string[] | undefined,
  isRcaOwnerOrAdmin?: boolean,
  isCapaOwnerOrAdmin?: boolean,
  isVerificationOwnerOrAdmin?: boolean,
  isRcaInvestigation?: boolean,
): readonly CmtsMenuGroup[] {
  const permissionSet = new Set(permissions ?? []);
  const routeByPath = new Map(cmtsEnterpriseMenuGroups.flatMap((group) => group.items.map((item) => [item.path, { group, item }] as const)));
  const grouped = new Map<string, { label: string; items: CmtsMenuItem[] }>();
  const orderedItems = backendItems.slice().sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999));

  for (const backendItem of orderedItems) {
    const path = backendItem.href?.trim();
    if (!path) continue;
    const routeMatch = routeByPath.get(path);
    if (!routeMatch) continue;
    const permission = backendItem.permission ?? routeMatch.item.permission;
    if (permission && permission !== "PUBLIC" && !permissionSet.has(permission)) continue;
    const groupId = routeMatch.group.id;
    const current = grouped.get(groupId) ?? { label: routeMatch.group.label, items: [] };
    const icon = cmtsMenuIconById[backendItem.id] ?? routeMatch.item.icon;
    current.items.push({
      id: backendItem.id || routeMatch.item.id,
      label: backendItem.label || routeMatch.item.label,
      path,
      permission: permission ?? routeMatch.item.permission,
      description: routeMatch.item.description,
      icon,
    });
    grouped.set(groupId, current);
  }

  // Ensure RCA / CAPA canonical menu items are populated ONLY for RCA Owner / Platform Admin
  const rcaManifestGroup = cmtsEnterpriseMenuGroups.find((g) => g.id === "rca-capa");
  if (rcaManifestGroup && isRcaOwnerOrAdmin) {
    grouped.set("rca-capa", {
      label: rcaManifestGroup.label,
      items: rcaManifestGroup.items.map((item) => ({
        ...item,
        icon: cmtsMenuIconById[item.id] || item.icon,
      })),
    });
  } else {
    grouped.delete("rca-capa");
  }

  // Ensure Investigation Navigation menu items are populated ONLY for RCA Lead / RCA Owner / Investigator (NOT Platform Admin)
  const invManifestGroup = cmtsEnterpriseMenuGroups.find((g) => g.id === "investigation-navigation");
  if (invManifestGroup && isRcaInvestigation) {
    grouped.set("investigation-navigation", {
      label: invManifestGroup.label,
      items: invManifestGroup.items.map((item) => ({
        ...item,
        icon: cmtsMenuIconById[item.id] || item.icon,
      })),
    });
  } else {
    grouped.delete("investigation-navigation");
  }

  // Ensure CAPA menu group is populated ONLY for CAPA Owner / Admin
  const capaManifestGroup = cmtsEnterpriseMenuGroups.find((g) => g.id === "capa");
  if (capaManifestGroup && isCapaOwnerOrAdmin) {
    if (!grouped.has("capa") || grouped.get("capa")!.items.length === 0) {
      grouped.set("capa", {
        label: capaManifestGroup.label,
        items: capaManifestGroup.items.map((item) => ({
          ...item,
          icon: cmtsMenuIconById[item.id] || item.icon,
        })),
      });
    }
  } else {
    grouped.delete("capa");
  }

  // Ensure Verification menu group is populated ONLY for Verifier / Admin
  const verificationManifestGroup = cmtsEnterpriseMenuGroups.find((g) => g.id === "verification");
  if (verificationManifestGroup && isVerificationOwnerOrAdmin) {
    if (!grouped.has("verification") || grouped.get("verification")!.items.length === 0) {
      grouped.set("verification", {
        label: verificationManifestGroup.label,
        items: verificationManifestGroup.items.map((item) => ({
          ...item,
          icon: cmtsMenuIconById[item.id] || item.icon,
        })),
      });
    }
  } else {
    grouped.delete("verification");
  }

  return Array.from(grouped.entries()).map(([id, value]) => ({ id, label: value.label, items: value.items }));
}

const ALLOWED_RCA_ADMIN_ROLES = new Set([
  "SYSTEM_ADMIN",
  "PLATFORM_ADMIN",
  "TENANT_ADMIN",
  "CMTS_ADMIN",
  "RCA_LEAD",
  "RCA_OWNER",
  "ROLE_DEMO_MFG_ADMIN",
  "ROLE_DEMO_MFG_INVESTIGATOR",
]);

export function isRcaOwnerOrPlatformAdminUser(
  roles?: readonly string[] | undefined,
  persona?: string | undefined,
  _permissions?: readonly string[] | undefined
): boolean {
  const roleList = (roles ?? []).map((r) => String(r).trim().toUpperCase());
  const personaStr = String(persona ?? "").trim().toUpperCase();

  // If user is exclusively a CAPA Owner or Verifier without explicit RCA/Admin roles, exclude from RCA
  const isExplicitCapaOrVerifier =
    roleList.includes("CAPA_OWNER") ||
    roleList.includes("VERIFICATION_OWNER") ||
    personaStr.includes("CAPA OWNER") ||
    personaStr.includes("VERIFIER");

  const isExplicitAdminOrRca =
    roleList.some((r) => r === "SYSTEM_ADMIN" || r === "PLATFORM_ADMIN" || r === "TENANT_ADMIN" || r === "CMTS_ADMIN" || r === "RCA_LEAD" || r === "RCA_OWNER") ||
    personaStr.includes("SYSTEM ADMIN") ||
    personaStr.includes("PLATFORM ADMIN") ||
    personaStr.includes("TENANT ADMIN") ||
    personaStr.includes("RCA LEAD") ||
    personaStr.includes("RCA OWNER");

  if (isExplicitCapaOrVerifier && !isExplicitAdminOrRca) {
    return false;
  }

  // 1. Direct match on allowed role codes
  const hasAllowedRole = roleList.some((role) => ALLOWED_RCA_ADMIN_ROLES.has(role));
  if (hasAllowedRole) return true;

  // 2. Direct match on persona strings
  if (
    personaStr.includes("SYSTEM ADMINISTRATOR") ||
    personaStr.includes("TENANT ADMINISTRATOR") ||
    personaStr.includes("PLATFORM ADMINISTRATOR") ||
    personaStr.includes("SYSTEM ADMIN") ||
    personaStr.includes("TENANT ADMIN") ||
    personaStr.includes("PLATFORM ADMIN") ||
    personaStr.includes("RCA LEAD") ||
    personaStr.includes("RCA OWNER")
  ) {
    return true;
  }

  return false;
}

const STRICT_RCA_INVESTIGATION_ROLES = new Set([
  "RCA_LEAD",
  "RCA_OWNER",
  "ROLE_DEMO_MFG_INVESTIGATOR",
]);

export function isRcaInvestigationUser(
  roles?: readonly string[] | undefined,
  persona?: string | undefined,
  _permissions?: readonly string[] | undefined
): boolean {
  const roleList = (roles ?? []).map((r) => String(r).trim().toUpperCase());
  const personaStr = String(persona ?? "").trim().toUpperCase();

  // 1. Direct match on strict RCA investigation role codes
  const hasStrictRcaRole = roleList.some((role) => STRICT_RCA_INVESTIGATION_ROLES.has(role));
  if (hasStrictRcaRole) return true;

  // 2. Persona check: if persona is explicitly an admin (and not RCA), exclude
  if (
    (personaStr.includes("ADMIN") || personaStr.includes("ADMINISTRATOR")) &&
    !personaStr.includes("RCA") &&
    !personaStr.includes("INVESTIGATOR")
  ) {
    return false;
  }

  // 3. Direct match on RCA persona strings
  if (
    personaStr.includes("RCA LEAD") ||
    personaStr.includes("RCA OWNER") ||
    personaStr.includes("INVESTIGATOR")
  ) {
    return true;
  }

  return false;
}

const STRICT_CAPA_ROLES = new Set([
  "CAPA_OWNER",
  "CAPA_LEAD",
  "ROLE_DEMO_MFG_CAPA_OWNER",
]);

const STRICT_ADMIN_ROLES = new Set([
  "SYSTEM_ADMIN",
  "PLATFORM_ADMIN",
  "TENANT_ADMIN",
  "CMTS_ADMIN",
  "ROLE_DEMO_MFG_ADMIN",
]);

export function isCapaOwnerOrPlatformAdminUser(
  roles?: readonly string[] | undefined,
  persona?: string | undefined,
  _permissions?: readonly string[] | undefined
): boolean {
  const roleList = (roles ?? []).map((r) => String(r).trim().toUpperCase());
  const personaStr = String(persona ?? "").trim().toUpperCase();

  // 1. Explicit admin check
  const isAdmin =
    roleList.some((role) => STRICT_ADMIN_ROLES.has(role)) ||
    personaStr.includes("SYSTEM ADMINISTRATOR") ||
    personaStr.includes("TENANT ADMINISTRATOR") ||
    personaStr.includes("PLATFORM ADMINISTRATOR") ||
    personaStr.includes("SYSTEM ADMIN") ||
    personaStr.includes("TENANT ADMIN") ||
    personaStr.includes("PLATFORM ADMIN");

  if (isAdmin) {
    return true;
  }

  // 2. Strictly disallow Verifier or other non-CAPA roles
  const isVerifierOrNonCapa =
    roleList.includes("VERIFICATION_OWNER") ||
    roleList.includes("RCA_LEAD") ||
    roleList.includes("CUSTOMER_USER") ||
    roleList.includes("SUPPLIER_USER") ||
    personaStr.includes("VERIFIER") ||
    personaStr.includes("VERIFICATION");

  if (isVerifierOrNonCapa && !roleList.includes("CAPA_OWNER")) {
    return false;
  }

  // 3. Strict CAPA Owner check
  const hasCapaRole = roleList.some((role) => STRICT_CAPA_ROLES.has(role));
  if (hasCapaRole) return true;

  if (
    personaStr === "CAPA OWNER" ||
    personaStr === "CAPA_OWNER" ||
    personaStr === "CAPA LEAD" ||
    personaStr.includes("CAPA OWNER") ||
    personaStr.includes("CAPA LEAD")
  ) {
    return true;
  }

  return false;
}

const STRICT_VERIFICATION_ROLES = new Set([
  "VERIFICATION_OWNER",
  "VERIFIER",
  "ROLE_DEMO_MFG_VERIFIER",
]);

export function isVerificationOwnerOrPlatformAdminUser(
  roles?: readonly string[] | undefined,
  persona?: string | undefined,
  _permissions?: readonly string[] | undefined
): boolean {
  const roleList = (roles ?? []).map((r) => String(r).trim().toUpperCase());
  const personaStr = String(persona ?? "").trim().toUpperCase();

  // 1. Explicit admin check
  const isAdmin =
    roleList.some((role) => STRICT_ADMIN_ROLES.has(role)) ||
    personaStr.includes("SYSTEM ADMINISTRATOR") ||
    personaStr.includes("TENANT ADMINISTRATOR") ||
    personaStr.includes("PLATFORM ADMINISTRATOR") ||
    personaStr.includes("SYSTEM ADMIN") ||
    personaStr.includes("TENANT ADMIN") ||
    personaStr.includes("PLATFORM ADMIN");

  if (isAdmin) {
    return true;
  }

  // 2. Disallow other non-verifier roles (like CAPA Owner, RCA Lead, Customer, etc.)
  const isCapaOrRca =
    roleList.includes("CAPA_OWNER") ||
    roleList.includes("RCA_LEAD") ||
    roleList.includes("CUSTOMER_USER") ||
    roleList.includes("SUPPLIER_USER") ||
    personaStr.includes("CAPA OWNER") ||
    personaStr.includes("RCA LEAD");

  if (isCapaOrRca && !roleList.includes("VERIFICATION_OWNER")) {
    return false;
  }

  // 3. Strict Verification Owner check
  const hasVerificationRole = roleList.some((role) => STRICT_VERIFICATION_ROLES.has(role));
  if (hasVerificationRole) return true;

  if (
    personaStr === "VERIFIER" ||
    personaStr === "VERIFICATION OWNER" ||
    personaStr === "VERIFICATION_OWNER" ||
    personaStr.includes("VERIFIER") ||
    personaStr.includes("VERIFICATION OWNER")
  ) {
    return true;
  }

  return false;
}

export function filterRcaOwnerOrAdminMenuGroups(
  groups: readonly CmtsMenuGroup[],
  isRcaOwnerOrAdmin: boolean,
  isCapaOwnerOrAdmin: boolean = true,
  isVerificationOwnerOrAdmin: boolean = true,
  isRcaInvestigation?: boolean,
): readonly CmtsMenuGroup[] {
  let result = groups;
  if (!isRcaOwnerOrAdmin) {
    result = result.filter((g) => g.id !== "rca-capa");
  }
  if (!isRcaInvestigation) {
    result = result.filter((g) => g.id !== "investigation-navigation");
  }
  if (!isCapaOwnerOrAdmin) {
    result = result.filter((g) => g.id !== "capa");
  }
  if (!isVerificationOwnerOrAdmin) {
    result = result.filter((g) => g.id !== "verification");
  }
  return result;
}
const CUSTOMER_MENU_PATHS =
  new Set<string>([
    "/cmts/dashboards/customer",
    "/cmts/complaints",
    "/cmts/complaints/create",
    "/cmts/messages",
    "/cmts/notifications",
  ]);

const CUSTOMER_MENU_LABELS:
  Readonly<Record<string, string>> = {
  "/cmts/dashboards/customer":
    "My Dashboard",

  "/cmts/complaints":
    "My Complaints",

  "/cmts/complaints/create":
    "Create Complaint",

  "/cmts/messages":
    "My Messages",

  "/cmts/notifications":
    "Notifications",
};

export function filterCustomerUserMenuGroups(
  groups: readonly CmtsMenuGroup[],
): readonly CmtsMenuGroup[] {
  return groups
    .map((group): CmtsMenuGroup => {
      const customerItems =
        group.items
          .filter((item) => {
            const path =
              item.path
                .split("?")[0]
                ?.trim() ??
              item.path.trim();

            return CUSTOMER_MENU_PATHS.has(
              path,
            );
          })
          .map(
            (
              item,
            ): CmtsMenuItem => {
              const path =
                item.path
                  .split("?")[0]
                  ?.trim() ??
                item.path.trim();

              return {
                ...item,
                label:
                  CUSTOMER_MENU_LABELS[
                  path
                  ] ?? item.label,
              };
            },
          );

      return {
        ...group,
        items: customerItems,
      };
    })
    .filter(
      (group) =>
        group.items.length > 0,
    );
}

export function isQualityManagerUser(
  roles?: readonly string[] | undefined,
  persona?: string | undefined,
  _permissions?: readonly string[] | undefined
): boolean {
  const roleList = (roles ?? []).map((r) => String(r).trim().toUpperCase());
  const personaStr = String(persona ?? "").trim().toUpperCase();

  const isAdmin =
    roleList.some((role) => STRICT_ADMIN_ROLES.has(role)) ||
    personaStr.includes("SYSTEM ADMINISTRATOR") ||
    personaStr.includes("PLATFORM ADMINISTRATOR") ||
    personaStr.includes("TENANT ADMINISTRATOR") ||
    personaStr.includes("SYSTEM ADMIN") ||
    personaStr.includes("PLATFORM ADMIN") ||
    personaStr.includes("TENANT ADMIN");

  if (isAdmin) return false;

  return (
    roleList.includes("QUALITY_MANAGER") ||
    roleList.includes("COMPLAINT_MANAGER") ||
    roleList.includes("ROLE_DEMO_MFG_QUALITY_HEAD") ||
    roleList.includes("ROLE_DEMO_MFG_COMPLAINT_COORDINATOR") ||
    personaStr.includes("QUALITY MANAGER") ||
    personaStr.includes("COMPLAINT MANAGER")
  );
}

const QUALITY_MANAGER_ALLOWED_GROUP_IDS = new Set([
  "dashboards",
  "complaint-management",
  "my-work",
  "reports",
]);

export function filterQualityManagerMenuGroups(
  groups: readonly CmtsMenuGroup[]
): readonly CmtsMenuGroup[] {
  return groups
    .filter((g) => QUALITY_MANAGER_ALLOWED_GROUP_IDS.has(g.id))
    .map((group) => {
      if (group.id === "dashboards") {
        return {
          ...group,
          items: group.items.filter((item) =>
            item.path.includes("/dashboards/quality") ||
            item.path.includes("/dashboards/department") ||
            item.path.includes("/dashboards/personal") ||
            item.path.includes("/dashboards")
          ),
        };
      }
      return group;
    })
    .filter((g) => g.items.length > 0);
}